/* Conguitos · selector de vista exclusivo de Novedades editoriales. */
(() => {
  const selector = '[data-novedades-editorial]';

  function normaliseLayout(value) {
    return value === 'grid' ? 'grid' : 'editorial';
  }

  function applyLayout(root, requestedLayout) {
    const layout = normaliseLayout(requestedLayout);
    root.dataset.novedadesLayout = layout;

    root.querySelectorAll('[data-novedades-layout-toggle]').forEach((button) => {
      const active = button.dataset.novedadesLayoutToggle === layout;
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.toggleAttribute('aria-current', active);
    });
  }

  function initialise(root) {
    if (!root || root.dataset.novedadesInitialised === 'true') return;

    root.dataset.novedadesInitialised = 'true';
    applyLayout(root, root.dataset.novedadesLayout);

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-novedades-layout-toggle]');
      if (!button || !root.contains(button)) return;

      event.preventDefault();
      applyLayout(root, button.dataset.novedadesLayoutToggle);
    });
  }

  function initialiseAll(scope = document) {
    if (scope.matches?.(selector)) initialise(scope);
    scope.querySelectorAll?.(selector).forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();

/* Vídeos editoriales: el ciclo del elemento también cubre editor y filtros AJAX. */
(() => {
  const elementName = 'novedades-editorial-video';
  if (!window.customElements || customElements.get(elementName)) return;

  class NovedadesEditorialVideo extends HTMLElement {
    connectedCallback() {
      if (this.removeListeners) return;

      this.videos = [...this.querySelectorAll('video[data-editorial-video]')];
      this.toggleButton = this.querySelector('[data-editorial-video-toggle]');
      if (!this.videos.length || !this.toggleButton) return;

      this.section = this.closest('[data-novedades-editorial]');
      this.mobileQuery = window.matchMedia('(max-width: 819px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.manualPlayback ??= null;
      this.inViewport = typeof IntersectionObserver === 'undefined';
      this.removeListeners = [];

      const listen = (target, type, handler) => {
        target.addEventListener(type, handler);
        this.removeListeners.push(() => target.removeEventListener(type, handler));
      };
      const reconcile = () => this.reconcilePlayback();

      listen(this.toggleButton, 'click', (event) => {
        event.preventDefault();
        const playingOrPending = !this.activeVideo.paused || this.pendingPlay;
        this.manualPlayback = !playingOrPending;
        this.reconcilePlayback();
      });
      listen(this.mobileQuery, 'change', reconcile);
      listen(this.motionQuery, 'change', reconcile);
      listen(document, 'visibilitychange', reconcile);

      this.videos.forEach((video) => {
        const update = (event) => {
          if (event.type === 'ended' && video === this.activeVideo) {
            // Un vídeo sin bucle termina hasta la siguiente petición manual.
            this.manualPlayback = false;
          }
          if (event.type === 'error' ||
              ((event.type === 'play' || event.type === 'playing') &&
                (video !== this.activeVideo || !this.shouldPlay()))) {
            video.pause();
          }
          this.updateButton();
        };
        ['play', 'playing', 'pause', 'ended', 'error'].forEach((type) => listen(video, type, update));
      });

      if (typeof IntersectionObserver !== 'undefined') {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.inViewport = entry.isIntersecting;
          this.reconcilePlayback();
        });
        this.visibilityObserver.observe(this);
      }
      if (this.section) {
        this.layoutObserver = new MutationObserver(reconcile);
        this.layoutObserver.observe(this.section, {
          attributes: true,
          attributeFilter: ['data-novedades-layout'],
        });
      }

      this.reconcilePlayback();
      this.toggleButton.hidden = false;
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.layoutObserver?.disconnect();
      this.removeListeners?.forEach((remove) => remove());
      this.removeListeners = null;
      this.pendingPlay = null;
      this.videos?.forEach((video) => video.pause());
      this.activeVideo = null;
      if (this.toggleButton) {
        this.toggleButton.hidden = true;
        this.toggleButton.setAttribute('aria-pressed', 'false');
      }
    }

    shouldPlay() {
      const requested = this.manualPlayback ??
        (this.dataset.autoplay === 'true' && !this.motionQuery.matches);
      return requested && this.isConnected && this.inViewport && !document.hidden &&
        this.section?.dataset.novedadesLayout !== 'grid';
    }

    reconcilePlayback() {
      if (!this.isConnected || !this.removeListeners) return;

      const mobileVideo = this.videos.find((video) => video.dataset.editorialVideo === 'mobile');
      const desktopVideo = this.videos.find((video) => video.dataset.editorialVideo === 'desktop');
      const nextVideo = (this.mobileQuery.matches && mobileVideo) || desktopVideo || this.videos[0];
      if (this.activeVideo !== nextVideo) {
        this.pendingPlay = null;
        this.activeVideo = nextVideo;
      }
      this.videos.forEach((video) => {
        if (video !== this.activeVideo) video.pause();
      });

      if (!this.shouldPlay()) {
        this.pendingPlay = null;
        this.activeVideo.pause();
      } else if (this.activeVideo.paused && !this.pendingPlay) {
        this.startPlayback(this.activeVideo);
      }
      this.updateButton();
    }

    startPlayback(video) {
      const request = { video };
      this.pendingPlay = request;

      // pause(), cambios de variante y recargas pueden cancelar play().
      const settled = () => {
        if (this.pendingPlay !== request) return;
        this.pendingPlay = null;
        if (video !== this.activeVideo || !this.shouldPlay()) video.pause();
        this.updateButton();
      };
      try {
        Promise.resolve(video.play()).then(settled, settled);
      } catch (error) {
        settled();
      }
    }

    updateButton() {
      if (!this.activeVideo || !this.toggleButton) return;
      const playing = !this.activeVideo.paused && !this.activeVideo.ended;
      this.toggleButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
      this.toggleButton.setAttribute('aria-controls', this.activeVideo.id);
    }
  }

  customElements.define(elementName, NovedadesEditorialVideo);
})();
